import {
  AuthenticationResult,
  Configuration,
  EventMessage,
  EventType,
  InteractionRequiredAuthError,
  IPublicClientApplication,
  PublicClientApplication,
  RedirectRequest,
  SilentRequest,
} from "@azure/msal-browser";
import getadb2cauth from "../../getadb2cauth";
import { getAuthErrorPath, getErrorMessage } from "../authErrors";
import { clearBrowserStorage } from "../authStorage";
import { getTokenExpirationTime, isTokenValid } from "../tokenValidation";
import { IAuthAdapter, TAuthAdapterCallbacks, TAuthConfig } from "../types";

const REFRESH_BEFORE_EXPIRATION_MS = 2 * 60 * 1000;
const MINIMUM_REFRESH_DELAY_MS = 1000;

export class WebAuthAdapter implements IAuthAdapter {
  private callbacks: TAuthAdapterCallbacks | null = null;
  private config: TAuthConfig | null = null;
  private msalInstance: IPublicClientApplication | null = null;
  private eventCallbackId: string | null = null;
  private refreshTimerId: number | null = null;
  private refreshPromise: Promise<string> | null = null;
  private currentIdToken = "";
  private isDisposed = false;

  private readonly handlePageActive = () => {
    if (document.visibilityState === "hidden" || !this.shouldRefreshToken()) return;

    void this.refreshTokenSafely();
  };

  async initialize(callbacks: TAuthAdapterCallbacks) {
    this.isDisposed = false;
    this.callbacks = callbacks;
    this.config = (await getadb2cauth()) as TAuthConfig;
    if (this.isDisposed) return;

    this.msalInstance = new PublicClientApplication(this.createMsalConfig(this.config));

    await this.msalInstance.initialize();
    if (this.isDisposed) return;

    this.registerEventCallbacks();
    document.addEventListener("visibilitychange", this.handlePageActive);
    window.addEventListener("focus", this.handlePageActive);
    await this.processAuthentication();
  }

  async login() {
    const msalInstance = this.getMsalInstance();
    const config = this.getConfig();
    const loginRequest: RedirectRequest = {
      scopes: config.scopes,
      redirectUri: config.redirectUri,
      prompt: "select_account",
    };

    await msalInstance.loginRedirect(loginRequest);
  }

  async logout() {
    const msalInstance = this.getMsalInstance();
    const account = msalInstance.getActiveAccount();

    if (!account) return;

    try {
      await msalInstance.logoutRedirect({
        account,
        postLogoutRedirectUri: this.getConfig().redirectUri,
      });
    } catch (error: unknown) {
      console.error("Erro ao fazer logout:", error);
      clearBrowserStorage();
      window.location.reload();
    }
  }

  refreshToken(): Promise<string> {
    if (this.refreshPromise) return this.refreshPromise;

    const currentPromise = this.acquireFreshToken();
    this.refreshPromise = currentPromise;

    currentPromise.then(
      () => {
        if (this.refreshPromise === currentPromise) this.refreshPromise = null;
      },
      () => {
        if (this.refreshPromise === currentPromise) this.refreshPromise = null;
      },
    );

    return currentPromise;
  }

  private async acquireFreshToken(): Promise<string> {
    const msalInstance = this.getMsalInstance();
    const account = msalInstance.getActiveAccount();

    if (!account) {
      await this.login();
      return "";
    }

    let response: AuthenticationResult;

    try {
      response = await msalInstance.acquireTokenSilent({
        account,
        scopes: this.getConfig().scopes,
        forceRefresh: true,
      });
    } catch (error: unknown) {
      if (error instanceof InteractionRequiredAuthError) {
        await this.login();
        return "";
      }

      throw error;
    }

    await this.completeAuthentication(response);
    return response.idToken;
  }

  dispose() {
    this.isDisposed = true;

    if (this.msalInstance && this.eventCallbackId) {
      this.msalInstance.removeEventCallback(this.eventCallbackId);
    }

    document.removeEventListener("visibilitychange", this.handlePageActive);
    window.removeEventListener("focus", this.handlePageActive);
    this.clearRefreshTimer();

    this.eventCallbackId = null;
    this.currentIdToken = "";
    this.callbacks = null;
  }

  private createMsalConfig(config: TAuthConfig): Configuration {
    return {
      auth: {
        clientId: config.applicationId,
        authority: `${config.instance}/${config.tenant}/${config.signInPolicy}`,
        knownAuthorities: [config.instance],
        redirectUri: config.redirectUri,
        postLogoutRedirectUri: config.redirectUri,
      },
      cache: {
        cacheLocation: "sessionStorage",
      },
    };
  }

  private registerEventCallbacks() {
    const msalInstance = this.getMsalInstance();

    this.eventCallbackId = msalInstance.addEventCallback((event: EventMessage) => {
      switch (event.eventType) {
        case EventType.LOGIN_SUCCESS:
        case EventType.ACQUIRE_TOKEN_SUCCESS: {
          const result = event.payload as AuthenticationResult | null;
          if (result?.account) msalInstance.setActiveAccount(result.account);
          break;
        }

        case EventType.ACQUIRE_TOKEN_FAILURE:
          this.callbacks?.onError(event.error);
          break;

        case EventType.LOGOUT_SUCCESS:
          this.callbacks?.onLoggedOut();
          break;
      }
    });
  }

  private async processAuthentication() {
    const msalInstance = this.getMsalInstance();

    try {
      const redirectResponse = await msalInstance.handleRedirectPromise({
        navigateToLoginRequestUrl: true,
      });
      if (this.isDisposed) return;

      if (redirectResponse) {
        await this.completeAuthentication(redirectResponse);
      } else {
        await this.restoreSession();
      }
    } catch (error: unknown) {
      if (this.isDisposed) return;

      const errorMessage = getErrorMessage(error);

      if (errorMessage.includes("AADB2C90118")) {
        await this.redirectToPasswordReset();
        return;
      }

      clearBrowserStorage();
      window.location.replace(getAuthErrorPath(errorMessage));
    }
  }

  private async restoreSession() {
    const msalInstance = this.getMsalInstance();
    const accounts = msalInstance.getAllAccounts();

    if (accounts.length === 0) {
      await this.login();
      return;
    }

    const account = msalInstance.getActiveAccount() || accounts[0];
    msalInstance.setActiveAccount(account);

    const tokenRequest: SilentRequest = {
      account,
      scopes: this.getConfig().scopes,
      forceRefresh: false,
    };

    try {
      let response = await msalInstance.acquireTokenSilent(tokenRequest);
      if (this.isDisposed) return;

      if (!isTokenValid(response.idToken, false)) {
        response = await msalInstance.acquireTokenSilent({
          ...tokenRequest,
          forceRefresh: true,
        });
        if (this.isDisposed) return;
      }

      await this.completeAuthentication(response);
    } catch (error: unknown) {
      if (this.isDisposed) return;

      if (error instanceof InteractionRequiredAuthError) {
        await this.login();
      } else {
        await this.clearCacheAndLogin();
      }
    }
  }

  private async completeAuthentication(response: AuthenticationResult) {
    if (this.isDisposed) return;

    if (!response.idToken || !isTokenValid(response.idToken, false)) {
      await this.clearCacheAndLogin();
      return;
    }

    const msalInstance = this.getMsalInstance();

    msalInstance.setActiveAccount(response.account);

    this.currentIdToken = response.idToken;

    this.scheduleTokenRefresh(response.idToken);

    this.callbacks?.onAuthenticated({
      msalInstance,
      idToken: response.idToken,
      isMobileAuth: false,
    });
  }

  private scheduleTokenRefresh(idToken: string) {
    this.clearRefreshTimer();

    const expirationTime = getTokenExpirationTime(idToken);
    if (!expirationTime || this.isDisposed) return;

    const refreshDelay = Math.max(
      expirationTime - Date.now() - REFRESH_BEFORE_EXPIRATION_MS,
      MINIMUM_REFRESH_DELAY_MS,
    );

    this.refreshTimerId = window.setTimeout(() => {
      void this.refreshTokenSafely();
    }, refreshDelay);
  }

  private clearRefreshTimer() {
    if (this.refreshTimerId === null) return;

    window.clearTimeout(this.refreshTimerId);
    this.refreshTimerId = null;
  }

  private shouldRefreshToken() {
    if (!this.currentIdToken) return false;

    const expirationTime = getTokenExpirationTime(this.currentIdToken);

    return !expirationTime || expirationTime - Date.now() <= REFRESH_BEFORE_EXPIRATION_MS;
  }

  private async refreshTokenSafely() {
    if (this.isDisposed) return;

    try {
      await this.refreshToken();
    } catch (error: unknown) {
      if (this.isDisposed) return;

      if (error instanceof InteractionRequiredAuthError) {
        await this.login();
        return;
      }

      this.callbacks?.onError(error);
    }
  }

  private async clearCacheAndLogin() {
    const msalInstance = this.getMsalInstance();

    try {
      for (const account of msalInstance.getAllAccounts()) {
        await msalInstance.clearCache({ account });
      }

      clearBrowserStorage();

      await new Promise<void>((resolve) => window.setTimeout(resolve, 100));

      if (this.isDisposed) return;

      await this.login();
    } catch (error: unknown) {
      console.error("Erro ao limpar cache:", error);
    }
  }

  private async redirectToPasswordReset() {
    const config = this.getConfig();

    await this.getMsalInstance().loginRedirect({
      authority: `${config.instance}/${config.tenant}/${config.resetPolicy}`,
      redirectUri: config.redirectUri,
      scopes: [],
    });
  }

  private getMsalInstance() {
    if (!this.msalInstance) throw new Error("MSAL nao inicializado");

    return this.msalInstance;
  }

  private getConfig() {
    if (!this.config) throw new Error("Configuracao de autenticacao nao carregada");

    return this.config;
  }
}
